import { useEffect, useState, type ReactNode } from 'react';
import type { VersionAction } from '../net/version.ts';

declare const __CLIENT_VERSION__: { build: string };

/** Keep deployment changes visible until a safe reload succeeds. */
export function VersionNotice(): ReactNode {
  const [update, setUpdate] = useState<{ action: VersionAction; build: string | null } | null>(null);
  const [warning, setWarning] = useState('');
  const [cacheWarning, setCacheWarning] = useState(false);
  useEffect(() => {
    const receive = (event: Event): void => setUpdate((event as CustomEvent<NonNullable<typeof update>>).detail);
    const cacheError = (event: MessageEvent): void => { if (event.data?.k === 'nightshift-cache-error') setCacheWarning(true); };
    window.addEventListener('nightshift-version', receive);
    if (import.meta.env.PROD && 'serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', cacheError);
      void navigator.serviceWorker.register(`/sw.js?build=${__CLIENT_VERSION__.build}`, { updateViaCache: 'none' }).catch((error) => { console.warn('Nightshift service worker registration failed:', error); setCacheWarning(true); });
    }
    return () => { window.removeEventListener('nightshift-version', receive); navigator.serviceWorker?.removeEventListener('message', cacheError); };
  }, []);
  useEffect(() => {
    setWarning('');
    if (!update || update.action === 'wait' || update.action === 'invalid') return;
    try {
      if (update.action === 'current') {
        sessionStorage.removeItem('nightshift-reloaded-build');
        return;
      }
      if (sessionStorage.getItem('nightshift-reloaded-build') === update.build) {
        setWarning('Reloading did not update this page. Try again in a minute.');
        return;
      }
    } catch (error) {
      console.warn('Automatic deployment refresh unavailable:', error);
      setWarning('This page cannot reload itself because your browser blocks storage. Please reload it yourself.');
      return;
    }
    const timer = window.setTimeout(() => {
      try {
        sessionStorage.setItem('nightshift-reloaded-build', update.build!);
        location.reload();
      } catch (error) {
        console.warn('Automatic deployment refresh unavailable:', error);
        setWarning('This page cannot reload itself because your browser blocks storage. Please reload it yourself.');
      }
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [update?.action, update?.build]);
  const mismatch = update && update.action !== 'current';
  if (!mismatch && !cacheWarning) return null;
  return (
    <aside className="toast" role="status" style={{ position: 'fixed', bottom: 80, left: 12, right: 12, zIndex: 60, maxWidth: 620, margin: 'auto' }}>
      {mismatch && <><strong>{update.action === 'invalid' ? 'Could not check the game version' : 'A new version is ready'}</strong>
      <p>{warning || (update.action === 'invalid' ? 'Your changes are on hold. Reload the page to reconnect.' : update.action === 'wait' ? 'Your changes are on hold. The page will reload itself at the next Review; reload now to carry on straight away.' : 'Reloading now. Your game is saved.')}</p></>}
      {cacheWarning && <p><strong>Offline copy unavailable</strong>. You can still play while online. Reload to try again.</p>}
      <button className="btn" onClick={() => location.reload()}>Reload now</button>
    </aside>
  );
}
